import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '@/store';
import { updateUser } from '@/store/authSlice';
import { useTabHistory } from '@/hooks/useTabHistory';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Divider,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
} from '@mui/material';
import {
  Save,
  AccountCircle,
  PhotoCamera,
  CheckCircle,
  Person,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  Place as PlaceIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AddCircleOutline as NewMatchIcon,
  ClearAll as ClearAllIcon,
  Close as CloseIcon,
  Sync as SyncIcon,
  SportsScore as SportsScoreIcon,
} from '@mui/icons-material';
import { TimezonePreference } from '@/components/TimezonePreference';
import LinkedAccounts from '@/components/LinkedAccounts';
import NotificationSettings from '@/components/settings/NotificationSettings';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { formatDistanceToNow, format } from 'date-fns';
import {
  getNotifications,
  markAllAsRead,
  clearAllNotifications,
  deleteNotification,
  ScheduleNotification,
  ScheduleNotificationType,
} from '@/services/notificationService';
import { useNotifications } from '@/contexts/EventStreamContext';
import { clearPushNotifications } from '@/utils/clearPushNotifications';
import { Pagination } from '@mui/material';
import { PreloadedAvatar } from '@/components/common/PreloadedAvatar';
import { getUserPhotoUrl } from '@/utils/photoUtils';
import AddressValidationInput, { AddressInput } from '@/components/AddressValidationInput';
import AddressAutocompleteInput from '@/components/AddressAutocompleteInput';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { IS_ADMIN_PORTAL } from '@/utils/adminDetection';

const NOTIFICATION_ICONS: Record<ScheduleNotificationType, React.ElementType> = {
  MATCH_DATE_CHANGED: EventIcon,
  MATCH_TIME_CHANGED: ScheduleIcon,
  MATCH_LOCATION_CHANGED: PlaceIcon,
  MATCH_NOW_SCHEDULED: CheckCircleIcon,
  MATCH_NOW_PENDING: WarningIcon,
  CONFLICT_DETECTED: WarningIcon,
  CONFLICT_RESOLVED: CheckCircleIcon,
  NEW_MATCH_ADDED: NewMatchIcon,
  SYNC_COMPLETE: SyncIcon,
  SCORE_POSTED: SportsScoreIcon,
};

const NOTIFICATION_COLORS: Record<ScheduleNotificationType, string> = {
  MATCH_DATE_CHANGED: '#1976d2',
  MATCH_TIME_CHANGED: '#f57c00',
  MATCH_LOCATION_CHANGED: '#7b1fa2',
  MATCH_NOW_SCHEDULED: '#388e3c',
  MATCH_NOW_PENDING: '#d32f2f',
  CONFLICT_DETECTED: '#d32f2f',
  CONFLICT_RESOLVED: '#388e3c',
  NEW_MATCH_ADDED: '#00897b',
  SYNC_COMPLETE: '#1565c0',
  SCORE_POSTED: '#2e7d32',
};

const getChangeDescription = (notification: ScheduleNotification): string => {
  const details = notification.changeDetails as Record<string, any> | null;

  // Combined notification (multiple fields changed at once)
  // New format: { homeTeam, awayTeam, changes: [...] }, old format: [...]
  const changesArray = Array.isArray(details) ? details : details?.changes;
  if (Array.isArray(changesArray)) {
    const fieldMap: Record<string, string> = {};
    for (const d of changesArray) {
      if (d.field && d.newValue) fieldMap[d.field] = d.newValue;
    }
    const parts: string[] = [];
    if (fieldMap.date) parts.push(`on ${fieldMap.date}`);
    if (fieldMap.time) parts.push(`at ${fieldMap.time}`);
    // Venue rendered separately
    if (parts.length > 0) return `Now ${parts.join(' ')}`;
    if (fieldMap.location) return `Moved to ${fieldMap.location}`;
    return 'Multiple fields updated';
  }

  switch (notification.type) {
    case 'MATCH_DATE_CHANGED':
      return details?.newValue ? `Rescheduled to ${details.newValue}` : 'Date changed';
    case 'MATCH_TIME_CHANGED':
      return details?.newValue ? `New time: ${details.newValue}` : 'Time changed';
    case 'MATCH_LOCATION_CHANGED':
      // Venue details rendered on separate line — no extra description needed
      return '';
    case 'MATCH_NOW_SCHEDULED': {
      // Message has complete info as "date, time @ venue" — strip venue
      const msg = notification.message || '';
      const atIdx = msg.indexOf(' @ ');
      return atIdx > 0 ? msg.slice(0, atIdx) : msg || 'Match has been scheduled';
    }
    case 'MATCH_NOW_PENDING':
      return 'New date/time TBD';
    case 'CONFLICT_DETECTED':
      return 'Schedule conflict detected';
    case 'CONFLICT_RESOLVED':
      return 'Schedule conflict resolved';
    case 'NEW_MATCH_ADDED': {
      // Strip venue from message (format: "date, time @ venue") — venue rendered separately
      const msg = notification.message || '';
      const atIdx = msg.indexOf(' @ ');
      return atIdx > 0 ? msg.slice(0, atIdx) : msg || 'New match added to schedule';
    }
    case 'SYNC_COMPLETE': {
      const parts: string[] = [];
      if (details?.matchesCreated) parts.push(`${details.matchesCreated} new`);
      if (details?.matchesUpdated) parts.push(`${details.matchesUpdated} updated`);
      if (details?.matchesDeleted) parts.push(`${details.matchesDeleted} removed`);
      return parts.length > 0 ? parts.join(', ') : 'No changes detected';
    }
    case 'SCORE_POSTED':
      return '';
    default:
      return 'Schedule updated';
  }
};

/** Returns venue info and whether it was a move (changed) or just context (@ venue) */
const getVenueInfo = (notification: ScheduleNotification): { venue: string; moved: boolean } | null => {
  const details = notification.changeDetails as Record<string, any> | null;

  // Combined notification — new format: { changes: [...] }, old format: [...]
  const changesArray = Array.isArray(details) ? details : details?.changes;
  if (Array.isArray(changesArray)) {
    const loc = changesArray.find((d: any) => d.field === 'location');
    return loc?.newValue ? { venue: loc.newValue, moved: true } : null;
  }

  switch (notification.type) {
    case 'MATCH_LOCATION_CHANGED':
      return details?.newValue ? { venue: details.newValue, moved: true } : null;
    case 'MATCH_NOW_SCHEDULED': {
      if (details?.location) return { venue: details.location, moved: false };
      // Fallback: parse venue from message ("date, time @ venue")
      const atIdx = notification.message?.indexOf(' @ ');
      if (atIdx && atIdx > 0) {
        return { venue: notification.message!.slice(atIdx + 3), moved: false };
      }
      return null;
    }
    case 'NEW_MATCH_ADDED': {
      const atIdx = notification.message?.indexOf(' @ ');
      if (atIdx && atIdx > 0) {
        return { venue: notification.message!.slice(atIdx + 3), moved: false };
      }
      return null;
    }
    default:
      return null;
  }
};

const NOTIFICATIONS_PAGE_SIZE = 10;

const UserSettingsPage: React.FC = () => {
  const { token, user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'profile';
  
  const { currentTab, changeTab } = useTabHistory({
    initialTab
  });
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(Date.now());
  const [useGravatar, setUseGravatar] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [processingInvitation, setProcessingInvitation] = useState<string | null>(null);
  const [distanceUnit, setDistanceUnit] = useState<'MILES' | 'KILOMETERS'>('MILES');
  const [savingDistanceUnit, setSavingDistanceUnit] = useState(false);
  const [temperatureUnit, setTemperatureUnit] = useState<'FAHRENHEIT' | 'CELSIUS'>('FAHRENHEIT');
  const [savingTemperatureUnit, setSavingTemperatureUnit] = useState(false);
  const [homeAddress, setHomeAddress] = useState<AddressInput>({ street: '', city: '', state: '', zip: '', country: 'US' });
  const [savedAddress, setSavedAddress] = useState<AddressInput>({ street: '', city: '', state: '', zip: '', country: 'US' });
  const [savingAddress, setSavingAddress] = useState(false);
  const { addressAutocompleteEnabled } = useFeatureFlags();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Compute user photo URL directly from user data — no API call needed
  const userPhotoUrl = React.useMemo(() => {
    if (!user || !token) return null;
    return getUserPhotoUrl(user, 'large', token);
  }, [user, token, photoRefreshKey]);

  // Notification state
  const queryClient = useQueryClient();
  const [notificationPage, setNotificationPage] = useState(1);
  const { resetUnreadCount } = useNotifications();

  // Fetch notifications
  const {
    data: notificationsData,
    isLoading: notificationsLoading,
    isFetching: notificationsFetching,
  } = useQuery(
    ['notifications', notificationPage],
    () => getNotifications(notificationPage, NOTIFICATIONS_PAGE_SIZE, false),
    {
      keepPreviousData: true,
      enabled: currentTab === 'notifications',
    }
  );

  // Auto-mark all as read when visiting the notifications tab
  useEffect(() => {
    if (currentTab === 'notifications') {
      resetUnreadCount();
      clearPushNotifications();
      markAllAsRead().then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      });
    }
  }, [currentTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear all notifications mutation
  const clearAllMutation = useMutation(() => clearAllNotifications(), {
    onSuccess: () => {
      resetUnreadCount();
      clearPushNotifications();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  // Delete single notification mutation
  const deleteNotificationMutation = useMutation(
    (notificationId: string) => deleteNotification(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      },
    }
  );

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setDistanceUnit(user.distanceUnit || 'MILES');
      setTemperatureUnit(user.temperatureUnit || 'FAHRENHEIT');
      setLoading(false);
    }
  }, [user]);

  // Load address from profile API
  useEffect(() => {
    if (!token) return;
    fetch('/api/v1/user/profile', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.ok ? res.json() : null)
    .then(response => {
      if (response?.data) {
        const d = response.data;
        if (d.homeAddressStreet || d.homeAddressCity || d.homeAddressState || d.homeAddressZip) {
          const addr = {
            street: d.homeAddressStreet || '',
            city: d.homeAddressCity || '',
            state: d.homeAddressState || '',
            zip: d.homeAddressZip || '',
            country: d.homeAddressCountry || 'US',
          };
          setHomeAddress(addr);
          setSavedAddress(addr);
        }
      }
    })
    .catch(() => {});
  }, [token]);

  // Fetch photo config to determine if using Gravatar
  useEffect(() => {
    if (!token) return;

    fetch('/api/v1/user/profile-photo/config', {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => res.ok ? res.json() : null)
    .then(response => {
      if (response?.data?.type) {
        setUseGravatar(response.data.type === 'GRAVATAR');
      }
    })
    .catch(() => {});
  }, [token]);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    if (!token) return;
    
    setLoadingInvitations(true);
    try {
      const response = await fetch('/api/v1/user/invitations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const { data } = await response.json();
        setInvitations(data);
      }
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!token) return;

    try {
      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, email })
      });

      if (response.ok) {
        const { data } = await response.json();
        dispatch(updateUser(data));
        setHasChanges(false);
        toast.success('Profile updated successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleChangePassword = async () => {
    if (!token) return;

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setChangingPassword(true);
    try {
      const response = await fetch('/api/v1/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (response.ok) {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        toast.success('Password changed successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to change password');
      }
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !token) return;

    const formData = new FormData();
    formData.append('photo', file);

    setUploadingPhoto(true);
    try {
      const response = await fetch('/api/v1/user/profile-photo/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        setPhotoRefreshKey(Date.now());
        window.dispatchEvent(new Event('userPhotoUpdated'));
        toast.success('Photo uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to upload photo');
      }
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleAcceptInvitation = async (invitationToken: string) => {
    setProcessingInvitation(invitationToken);
    try {
      const response = await fetch(`/api/v1/invitations/${invitationToken}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Invitation accepted successfully');
        loadInvitations();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to accept invitation');
      }
    } catch (error) {
      toast.error('Failed to accept invitation');
    } finally {
      setProcessingInvitation(null);
    }
  };

  const handleDistanceUnitChange = async (newUnit: 'MILES' | 'KILOMETERS') => {
    if (!token || newUnit === distanceUnit) return;

    setSavingDistanceUnit(true);
    try {
      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ distanceUnit: newUnit })
      });

      if (response.ok) {
        const { data } = await response.json();
        setDistanceUnit(newUnit);
        dispatch(updateUser({ distanceUnit: data.distanceUnit }));
        toast.success('Distance unit preference updated');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update distance unit preference');
      }
    } catch (error) {
      toast.error('Failed to update distance unit preference');
    } finally {
      setSavingDistanceUnit(false);
    }
  };

  const handleTemperatureUnitChange = async (newUnit: 'FAHRENHEIT' | 'CELSIUS') => {
    if (!token || newUnit === temperatureUnit) return;

    setSavingTemperatureUnit(true);
    try {
      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ temperatureUnit: newUnit })
      });

      if (response.ok) {
        const { data } = await response.json();
        setTemperatureUnit(newUnit);
        dispatch(updateUser({ temperatureUnit: data.temperatureUnit }));
        toast.success('Temperature unit preference updated');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update temperature unit preference');
      }
    } catch (error) {
      toast.error('Failed to update temperature unit preference');
    } finally {
      setSavingTemperatureUnit(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!token) return;

    setSavingAddress(true);
    try {
      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          homeAddressStreet: homeAddress.street || null,
          homeAddressCity: homeAddress.city || null,
          homeAddressState: homeAddress.state || null,
          homeAddressZip: homeAddress.zip || null,
        }),
      });

      if (response.ok) {
        setSavedAddress({ ...homeAddress });
        toast.success('Home address updated');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to update address');
      }
    } catch {
      toast.error('Failed to update address');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleAddressChange = (newAddress: AddressInput) => {
    setHomeAddress(newAddress);
  };

  const handleClearAddress = async () => {
    if (!token) return;
    setSavingAddress(true);
    try {
      const response = await fetch('/api/v1/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          homeAddressStreet: null,
          homeAddressCity: null,
          homeAddressState: null,
          homeAddressZip: null,
        }),
      });

      if (response.ok) {
        const empty = { street: '', city: '', state: '', zip: '', country: 'US' };
        setHomeAddress(empty);
        setSavedAddress(empty);
        toast.success('Home address cleared');
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to clear address');
      }
    } catch {
      toast.error('Failed to clear address');
    } finally {
      setSavingAddress(false);
    }
  };

  const renderProfileTab = () => (
    <>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Profile Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <PreloadedAvatar
                    src={userPhotoUrl}
                    size={120}
                    fallbackIcon={<AccountCircle sx={{ fontSize: 80 }} />}
                    sx={{
                      cursor: useGravatar ? 'default' : 'pointer'
                    }}
                    onClick={() => !useGravatar && fileInputRef.current?.click()}
                  />
                  {uploadingPhoto && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '50%'
                      }}
                    >
                      <CircularProgress size={40} sx={{ color: 'white' }} />
                    </Box>
                  )}
                  {!useGravatar && (
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        minWidth: 'auto',
                        width: 40,
                        height: 40,
                        borderRadius: '50%'
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                    >
                      <PhotoCamera />
                    </Button>
                  )}
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useGravatar}
                      onChange={async (e) => {
                        const newValue = e.target.checked;
                        setUseGravatar(newValue);
                        try {
                          const endpoint = newValue
                            ? '/api/v1/user/profile-photo/gravatar'
                            : '/api/v1/user/profile-photo/upload-mode';
                          const response = await fetch(endpoint, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (response.ok) {
                            setPhotoRefreshKey(Date.now());
                            toast.success(newValue ? 'Switched to Gravatar' : 'Switched to uploaded photo');
                          } else {
                            const error = await response.json();
                            toast.error(error.message || 'Failed to update photo preference');
                            setUseGravatar(!newValue); // Revert on error
                          }
                        } catch {
                          toast.error('Failed to update photo preference');
                          setUseGravatar(!newValue); // Revert on error
                        }
                      }}
                    />
                  }
                  label="Use Gravatar"
                />
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setHasChanges(true); }}
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setHasChanges(true); }}
                  fullWidth
                />
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveProfile}
                  disabled={!hasChanges}
                  sx={{ alignSelf: 'flex-end' }}
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Timezone Preferences
          </Typography>
          <TimezonePreference
            onSave={async (preference, timezone) => {
              try {
                const response = await fetch('/api/v1/user/profile', {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ 
                    timezonePreference: preference,
                    timezone: preference === 'custom' ? timezone : null
                  })
                });

                if (response.ok) {
                  const { data } = await response.json();
                  if (user && token) {
                    dispatch(updateUser({
                      timezonePreference: data.timezonePreference,
                      timezone: data.timezone
                    }));
                  }
                  toast.success('Timezone preference updated successfully');
                } else {
                  const error = await response.json();
                  toast.error(error.message || 'Failed to update timezone preference');
                }
              } catch (error) {
                toast.error('Failed to update timezone preference');
              }
            }}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Distance Unit
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose your preferred unit for displaying distances on maps
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <ToggleButtonGroup
              value={distanceUnit}
              exclusive
              onChange={(_, value) => value && handleDistanceUnitChange(value)}
              disabled={savingDistanceUnit}
            >
              <ToggleButton value="MILES" sx={{ px: 3 }}>
                Miles
              </ToggleButton>
              <ToggleButton value="KILOMETERS" sx={{ px: 3 }}>
                Kilometers
              </ToggleButton>
            </ToggleButtonGroup>
            {savingDistanceUnit && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Temperature Unit
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose your preferred unit for displaying temperatures
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <ToggleButtonGroup
              value={temperatureUnit}
              exclusive
              onChange={(_, value) => value && handleTemperatureUnitChange(value)}
              disabled={savingTemperatureUnit}
            >
              <ToggleButton value="FAHRENHEIT" sx={{ px: 3 }}>
                Fahrenheit (&deg;F)
              </ToggleButton>
              <ToggleButton value="CELSIUS" sx={{ px: 3 }}>
                Celsius (&deg;C)
              </ToggleButton>
            </ToggleButtonGroup>
            {savingTemperatureUnit && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Used to calculate travel time to matches
          </Typography>
          {addressAutocompleteEnabled ? (
            <AddressAutocompleteInput
              value={homeAddress}
              onChange={handleAddressChange}
              label=""
            />
          ) : (
            <AddressValidationInput
              value={homeAddress}
              onChange={handleAddressChange}
              onValidationChange={() => {}}
              label=""
            />
          )}
          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            {!!(savedAddress.street?.trim() || savedAddress.city?.trim() || savedAddress.state?.trim() || savedAddress.zip?.trim()) && (
              <Button
                variant="outlined"
                color="error"
                onClick={handleClearAddress}
                disabled={savingAddress}
              >
                Clear
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={savingAddress ? <CircularProgress size={20} /> : <Save />}
              onMouseDown={() => {
                if (document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur();
                }
              }}
              onClick={handleSaveAddress}
              disabled={
                savingAddress ||
                homeAddress.street === savedAddress.street && homeAddress.city === savedAddress.city && homeAddress.state === savedAddress.state && homeAddress.zip === savedAddress.zip ||
                !homeAddress.street?.trim() || !homeAddress.city?.trim() || !homeAddress.state?.trim() || !homeAddress.zip?.trim()
              }
            >
              Save
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Pending Invitations
          </Typography>
          {loadingInvitations ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress />
            </Box>
          ) : invitations.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending invitations
            </Typography>
          ) : (
            <List>
              {invitations.map((invitation) => (
                <React.Fragment key={invitation.id}>
                  <ListItem>
                    <ListItemText
                      primary={invitation.organization.name}
                      secondary={`Role: ${invitation.role} • Expires: ${new Date(invitation.expiresAt).toLocaleDateString()}`}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleAcceptInvitation(invitation.token)}
                        disabled={processingInvitation === invitation.token}
                        startIcon={processingInvitation === invitation.token ? <CircularProgress size={16} /> : <CheckCircle />}
                      >
                        Accept
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {invitation !== invitations[invitations.length - 1] && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </>
  );

  const renderSecurityTab = () => (
    <>
      <Typography variant="h6" gutterBottom>
        Change Password
      </Typography>
      <Box display="flex" flexDirection="column" gap={2} maxWidth={400} sx={{ mb: 3 }}>
        <TextField
          label="Current Password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          fullWidth
        />
        <TextField
          label="New Password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          fullWidth
        />
        <TextField
          label="Confirm New Password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          fullWidth
        />
        <Button
          variant="contained"
          onClick={handleChangePassword}
          disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
          startIcon={changingPassword ? <CircularProgress size={20} /> : <Save />}
          sx={{ alignSelf: 'flex-end' }}
        >
          Update
        </Button>
      </Box>

      <LinkedAccounts />
    </>
  );

  const notifications = notificationsData?.notifications || [];
  const totalNotificationPages = Math.ceil((notificationsData?.total || 0) / NOTIFICATIONS_PAGE_SIZE);

  const handleNotificationPageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setNotificationPage(value);
  };

  const renderNotificationsTab = () => (
    <>
    <NotificationSettings />
    <Card sx={{ mt: 3, overflow: 'hidden' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" gutterBottom>
            Notification History
          </Typography>
          {notifications.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={() => clearAllMutation.mutate()}
              disabled={clearAllMutation.isLoading}
            >
              Clear all
            </Button>
          )}
        </Box>
        <Divider />

        {/* Notification List */}
        {notificationsLoading ? (
          <List>
            {[...Array(5)].map((_, index) => (
              <ListItem key={index} divider>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: 'action.hover',
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={<Box component="span" sx={{ display: 'block', width: '60%', height: 20, bgcolor: 'action.hover', borderRadius: 1 }} />}
                  secondary={<Box component="span" sx={{ display: 'block', width: '80%', height: 16, bgcolor: 'action.hover', borderRadius: 1, mt: 1 }} />}
                />
              </ListItem>
            ))}
          </List>
        ) : notifications.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No notifications yet
            </Typography>
            <Typography color="text.secondary">
              You'll receive notifications when your schedule changes.
            </Typography>
          </Box>
        ) : (
          <Box>
            {notifications.map((notification, index) => {
              const Icon = NOTIFICATION_ICONS[notification.type] || NotificationsIcon;
              const iconColor = NOTIFICATION_COLORS[notification.type] || '#666';

              const handleClick = () => {
                if (notification.type === 'SYNC_COMPLETE') {
                  navigate('/leagues');
                  return;
                }
                const params = new URLSearchParams();
                const targetMatchId = notification.match?.id || notification.globalMatchId;
                if (targetMatchId) {
                  params.set('matchId', targetMatchId);
                }
                const details = notification.changeDetails as Record<string, any> | null;
                if (details?.homeTeam && details?.awayTeam) {
                  params.set('search', `${details.homeTeam} vs ${details.awayTeam}`);
                } else if (notification.match?.homeTeam) {
                  params.set('search', notification.match.homeTeam);
                }
                if (notification.match?.dateTime) {
                  const matchDate = new Date(notification.match.dateTime);
                  params.set('date', matchDate.toISOString().split('T')[0]);
                }
                const queryString = params.toString();
                if (queryString) navigate(`/schedule?${queryString}`);
              };

              return (
                <Box
                  key={notification.id}
                  onClick={handleClick}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    py: 2,
                    px: 2,
                    borderBottom: index < notifications.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ flexShrink: 0, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${iconColor}15`,
                      }}
                    >
                      <Icon sx={{ color: iconColor }} />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1">{notification.title}</Typography>
                    {notification.changeDetails?.homeTeam && (
                      <Typography variant="body2" color="text.primary">
                        {notification.changeDetails.homeTeam} vs {notification.changeDetails.awayTeam}
                      </Typography>
                    )}
                    {getChangeDescription(notification) && (
                      <Typography variant="body2" color="text.secondary">
                        {getChangeDescription(notification)}
                      </Typography>
                    )}
                    {(() => {
                      const venueInfo = getVenueInfo(notification);
                      if (!venueInfo) return null;
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {venueInfo.moved ? 'Moved to' : '@'} {venueInfo.venue}
                        </Typography>
                      );
                    })()}
                    {notification.match?.dateTime && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {format(new Date(notification.match.dateTime), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                      {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); deleteNotificationMutation.mutate(notification.id); }}
                    disabled={deleteNotificationMutation.isLoading}
                    sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}

        {/* Pagination */}
        {totalNotificationPages > 1 && (
          <>
            <Divider />
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={totalNotificationPages}
                page={notificationPage}
                onChange={handleNotificationPageChange}
                disabled={notificationsFetching}
                color="primary"
              />
            </Box>
          </>
        )}
      </CardContent>
    </Card>
    </>
  );

  if (loading) return null;

  return (
    <Box sx={{ display: 'flex', minHeight: IS_ADMIN_PORTAL ? '100%' : '100vh', mt: IS_ADMIN_PORTAL ? 0 : -8, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Vertical Sidebar Menu - Desktop only */}
      <Box sx={{
        display: { xs: 'none', md: 'block' },
        width: 240,
        flexShrink: 0,
        pr: 3,
        pl: 3,
        pt: IS_ADMIN_PORTAL ? 3 : 10
      }}>
        <Box component="nav">
          <Typography variant="h6" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
            Account
          </Typography>
          {([
            { tab: 'profile', label: 'Profile', icon: <Person /> },
            { tab: 'security', label: 'Security', icon: <SecurityIcon /> },
            { tab: 'notifications', label: 'Notifications', icon: <NotificationsIcon /> },
          ] as const).map(({ tab, label, icon }) => (
            <Button
              key={tab}
              fullWidth
              variant="text"
              sx={{
                justifyContent: 'flex-start',
                pl: 2,
                mb: 0.5,
                ...(currentTab === tab && {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }),
              }}
              onClick={() => changeTab(tab)}
              startIcon={icon}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Box>

      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      {/* Main Content */}
      <Box sx={{
        flexGrow: 1,
        minWidth: 0,
        maxWidth: { xs: '100%', md: 800 },
        pl: { xs: 2, md: 3 },
        pr: { xs: 2, md: 3 },
        pt: { xs: 2, md: IS_ADMIN_PORTAL ? 3 : 10 },
        pb: { xs: 10, md: 3 },
      }}>
        {/* Spacer for mobile app bar (regular app only — AdminLayout has its own) */}
        {!IS_ADMIN_PORTAL && (
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Toolbar />
          </Box>
        )}

        {/* Profile Tab */}
        {currentTab === 'profile' && (
          <>
            <Box display="flex" alignItems="center" sx={{ minHeight: 40, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Profile
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your personal information and preferences
            </Typography>
            {renderProfileTab()}
          </>
        )}

        {/* Security Tab */}
        {currentTab === 'security' && (
          <>
            <Box display="flex" alignItems="center" sx={{ minHeight: 40, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Security
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your account security and password
            </Typography>
            {renderSecurityTab()}
          </>
        )}

        {/* Notifications Tab */}
        {currentTab === 'notifications' && (
          <>
            <Box display="flex" alignItems="center" sx={{ minHeight: 40, mb: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Notifications
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage notification preferences and view history
            </Typography>
            {renderNotificationsTab()}
          </>
        )}
      </Box>

      {/* Bottom Navigation - Mobile only */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', md: 'none' },
          zIndex: 1000,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        elevation={3}
      >
        <BottomNavigation
          value={currentTab}
          onChange={(_, newValue) => {
            changeTab(newValue);
          }}
          showLabels
          sx={{ bgcolor: 'background.paper' }}
        >
          <BottomNavigationAction label="Profile" value="profile" icon={<Person />} />
          <BottomNavigationAction label="Security" value="security" icon={<SecurityIcon />} />
          <BottomNavigationAction label="Notifications" value="notifications" icon={<NotificationsIcon />} />
        </BottomNavigation>
      </Paper>

    </Box>
  );
};

export default UserSettingsPage;
